<template>
  <div class="generator-review card p-4 mb-3">
    <h4>{{ $t("signup.se.generator_review.company_address") }}</h4>
    <TextFieldComponent
      v-model="companyAddress.postal_address"
      field="signup.se.generator_review.postal_address"
      :rules="requiredRules"
    />
    <TextFieldComponent
      v-model="companyAddress.postal_code"
      field="signup.se.generator_review.postal_code"
      :rules="requiredRules"
    />
    <TextFieldComponent
      v-model="companyAddress.city"
      field="signup.se.generator_review.city"
      :rules="requiredRules"
    />

    <div class="form-check mb-3">
      <input
        type="checkbox"
        class="form-check-input"
        id="sameAsCompanyAddress"
        v-model="sameAsCompanyAddress"
      />
      <label class="form-check-label" for="sameAsCompanyAddress">
        {{ $t("signup.se.generator_review.same_as_company_address") }}
      </label>
    </div>

    <template v-if="!sameAsCompanyAddress && invoiceAddress">
      <h4>{{ $t("signup.se.generator_review.invoice_address") }}</h4>
      <TextFieldComponent
        v-model="invoiceAddress.postal_address"
        field="signup.se.generator_review.postal_address"
        :rules="requiredRules"
      />
      <TextFieldComponent
        v-model="invoiceAddress.postal_code"
        field="signup.se.generator_review.postal_code"
        :rules="requiredRules"
      />
      <TextFieldComponent
        v-model="invoiceAddress.city"
        field="signup.se.generator_review.city"
        :rules="requiredRules"
      />
      <TextFieldComponent
        v-model="invoiceAddress.country"
        field="signup.se.generator_review.invoice_country"
        :rules="requiredRules"
      />
      <TextFieldComponent
        v-model="invoiceAddress.co_address"
        field="signup.se.generator_review.invoice_co_address"
      />
      <TextFieldComponent
        v-model="invoiceAddress.email"
        field="signup.se.generator_review.invoice_email"
        :rules="requiredRules"
      />
      <TextFieldComponent
        v-model="invoiceAddress.specific_name"
        field="signup.se.generator_review.specific_name"
        :rules="requiredRules"
      />
      <TextFieldComponent
        v-model="invoiceAddress.telephone"
        field="signup.se.generator_review.invoice_contact_telephone"
        :rules="requiredRules"
      />
      <TextFieldComponent
        v-model="invoiceAddress.reference_number"
        field="signup.se.generator_review.reference_number"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { useValidators } from "@/composables/useValidators";
import TextFieldComponent from "@/signup/common/TextFieldComponent.vue";
import type { Account, Generator } from "@/types";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  generatorIndex: number;
  companyIndex: number;
  accountData: Account;
}>();

const emit = defineEmits<{
  handleUpdateGenerator: [generator: Generator];
}>();

const { t } = useI18n();
const validators = useValidators(t);

const generator = computed(
  () =>
    props.accountData.companies[props.companyIndex].generators[
      props.generatorIndex
    ]
);

// Determine initial checkbox state based on whether invoice_address exists
const sameAsCompanyAddress = ref(
  !generator.value.application_information?.invoice_address
);

// Initialize with empty address objects if they don't exist
const localGenerator = reactive({
  ...generator.value,
  application_information: {
    ...generator.value.application_information,
    full_company_address: {
      postal_address: "",
      postal_code: "",
      city: "",
      ...generator.value.application_information?.full_company_address,
    },
    // Only initialize invoice_address if it exists in generator.value
    invoice_address:
      generator.value.application_information?.invoice_address || null,
  },
});

const companyAddress = computed(
  () => localGenerator.application_information.full_company_address
);
const invoiceAddress = computed(
  () => localGenerator.application_information.invoice_address
);

// The invoice fields render only while the invoice address differs, so `required` there is conditional already.
const requiredRules = { required: validators.required };

watch(
  [localGenerator, sameAsCompanyAddress],
  ([newValue, isSameAddress]) => {
    if (isSameAddress) {
      // Set invoice_address to null when addresses are the same
      localGenerator.application_information.invoice_address = null;
    } else if (!localGenerator.application_information.invoice_address) {
      // Initialize invoice_address if it's null and addresses are different
      localGenerator.application_information.invoice_address = {
        postal_address: "",
        postal_code: "",
        city: "",
      };
    }
    emit("handleUpdateGenerator", newValue);
  },
  { deep: true }
);
</script>
